// # Email Verification Modal Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../state';
import { checkVerificationStatus, sendVerificationEmail } from './verify_user_email';
import './verif_modal.css';

const VerificationModal = ({ onClose }) => {
  // # Get auth state and actions
  const { user, emailVerified, setEmailVerified } = useAuthStore();
  
  // # Local state
  const [isChecking, setIsChecking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'info', 'success', 'error'
  const [isClosing, setIsClosing] = useState(false);
  
  // # Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);
  
  // # Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };
  
  // # Check verification status
  const handleCheckStatus = async () => {
    try {
      setIsChecking(true);
      setStatusMessage('Checking verification status...');
      setMessageType('info');
      
      // # Check status directly from Firebase
      const result = await checkVerificationStatus();
      
      if (result.success) {
        if (result.isVerified) {
          setStatusMessage('Your email has been verified! The page will refresh shortly.');
          setMessageType('success');
          setEmailVerified(true);
          
          // # Close modal and refresh page after delay
          setTimeout(() => {
            handleClose();
            setTimeout(() => {
              window.location.reload();
            }, 300);
          }, 2000);
        } else {
          setStatusMessage('Your email is not verified yet. Please check your inbox and click the verification link.');
          setMessageType('error');
        }
      } else {
        setStatusMessage('Error checking verification status. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setStatusMessage('An unexpected error occurred. Please try again.');
      setMessageType('error');
    } finally {
      setIsChecking(false);
    }
  };
  
  // # Send verification email
  const handleSendEmail = async () => {
    try {
      setIsSending(true);
      setStatusMessage('Sending verification email...');
      setMessageType('info');
      
      // # Send email
      const result = await sendVerificationEmail();
      
      if (result.success) {
        setStatusMessage('Verification email sent! Please check your inbox and follow the link to verify your email.');
        setMessageType('success');
      } else {
        setStatusMessage(`Failed to send verification email: ${result.error}`);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setStatusMessage('An unexpected error occurred. Please try again.');
      setMessageType('error');
    } finally {
      setIsSending(false);
    }
  };
  
  // # Remind me later
  const handleRemindLater = () => {
    // # Set a reminder in localStorage
    localStorage.setItem('verificationReminder', JSON.stringify({
      timestamp: Date.now(),
      reminded: true,
      nextReminder: Date.now() + (12 * 60 * 60 * 1000) // 12 hours
    }));
    
    // # Close modal with animation
    handleClose();
  };
  
  return (
    <div 
      className={`vrf_mdl_overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleClose}
    >
      <div 
        className={`vrf_mdl_modal ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="vrf_mdl_modal-header">
          <h2>Email Verification Required</h2>
          <button 
            className="vrf_mdl_close-btn" 
            onClick={handleClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        <div className="vrf_mdl_modal-body">
          <div className="vrf_mdl_icon-container">
            <i className="vrf_mdl_email-icon">
              {isChecking ? '🔄' : isSending ? '📨' : '✉️'}
            </i>
          </div>
          
          <p className="vrf_mdl_message">
            Please verify your email address{' '}
            <span className="vrf_mdl_email">
              ({user?.email})
            </span>{' '}
            to continue using all features of our platform, unverified users will be deleted after 72 to 96 hours.
          </p>
          
          {statusMessage && (
            <div 
              className={`vrf_mdl_status-message vrf_mdl_${messageType}`}
              role="alert"
            >
              {statusMessage}
            </div>
          )}
          
          <div className="vrf_mdl_actions">
            <button
              className="vrf_mdl_action-btn vrf_mdl_primary"
              onClick={handleCheckStatus}
              disabled={isChecking}
            >
              {isChecking ? (
                <span className="vrf_mdl_checking">
                  <span className="vrf_mdl_spinner"></span>
                  Checking...
                </span>
              ) : (
                "I've Verified My Email"
              )}
            </button>
            
            <button
              className="vrf_mdl_action-btn vrf_mdl_secondary"
              onClick={handleSendEmail}
              disabled={isSending}
            >
              {isSending ? (
                <span className="vrf_mdl_sending">
                  <span className="vrf_mdl_spinner"></span>
                  Sending...
                </span>
              ) : (
                'Resend Verification Email'
              )}
            </button>
            
            <button
              className="vrf_mdl_action-btn vrf_mdl_tertiary"
              onClick={handleRemindLater}
            >
              Remind Me Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
